import { useEffect, useState, useCallback, useRef } from "react";
import {
  useToast,
  Box,
  Heading,
  Button,
  Flex,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Grid,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { WarningIcon, RefreshIcon, SearchIcon, FilterIcon, DownloadIcon, ChevronDownIcon } from "../../utils/icons";
import { useInfractions } from "../../hooks/useInfractions";
import ImageModal from "./ImageModal";
import FilterDrawer from "./FilterDrawer";
import PlateInfractionCard from "./PlateInfractionCard";
import PeriodStatsTab from "./PeriodStatsTab";
import UserStatsTab from "./UserStatsTab";
import AnalysisTab from "./AnalysisTab";
import ValidationTab from "./ValidationTab";
import UploadInfractionsTab from "./UploadInfractionsTab";
import TrainingExportTab from "./TrainingExportTab";
import WhitelistTab from "../WhitelistTab";
import RegisteredPlatesTab from "./RegisteredPlatesTab";

const InfractionsMain = () => {
  const [cameras, setCameras] = useState([]);
  const [algorithms, setAlgorithms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedInfraction, setSelectedInfraction] = useState(null);
  const [userLevel, setUserLevel] = useState(1);
  const [externallyRestricted, setExternallyRestricted] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    cameraId: "",
    algorithmId: "",
    startDate: "",
    endDate: "",
    plate: "",
    infractionClass: "",
    reviewedBy: "",
    plateContains: false, // Adicionar o campo plateContains
    startTime: "", // novo: horário inicial (HH:MM)
    endTime: ""    // novo: horário final (HH:MM)
  });
  const [currentFilters, setCurrentFilters] = useState(null); // Para armazenar os filtros ativos
  const [currentInfractionIndex, setCurrentInfractionIndex] = useState(-1);
  const [isExporting, setIsExporting] = useState(false);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  const [isDraggingTabs, setIsDraggingTabs] = useState(false);
  const [tabsDragStartX, setTabsDragStartX] = useState(0);
  const [tabsScrollStartLeft, setTabsScrollStartLeft] = useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast(); const {
    infractions,
    filteredInfractions,
    loading,
    loadingMore,
    pagination,
    searchStats,
    fetchInfractions,
    fetchLast24HoursInfractions,
    searchInfractions,
    updateInfractionStatus,
    searchInfractionsLoadMore,
    fetchLast24HoursLoadMore,
    exportSearchData,
    exportToPDF
  } = useInfractions();
  const tabsCarouselRef = useRef(null);
  // Em acesso externo restrito: UI como nível 1, mas lista geral (somente leitura)
  const listUserLevel = externallyRestricted ? 3 : userLevel;
  const isReadOnlyView = externallyRestricted || userLevel < 2;
  const tabsItems = [
    { show: userLevel >= 2, label: "Estatísticas Temporais" },
    { show: userLevel > 2, label: "Estatísticas de Usuários" },
    { show: userLevel >= 3, label: "Análise de Usuários" },
    { show: userLevel >= 2, label: "Validação" },
    { show: true, label: "Lista de Infrações" },
    { show: userLevel >= 4, label: "Upload de infrações" },
    { show: userLevel >= 5, label: "Exportar" },
    { show: userLevel >= 2, label: "Placas Cadastradas" },
    { show: userLevel >= 2, label: "Whitelist" }
  ].filter((item) => item.show);

  const updateTabsScrollState = useCallback(() => {
    const node = tabsCarouselRef.current;
    if (!node) return;

    const maxLeft = node.scrollWidth - node.clientWidth;
    setCanScrollTabsLeft(node.scrollLeft > 2);
    setCanScrollTabsRight(maxLeft - node.scrollLeft > 2);
  }, []);

  const getTabsStepWidth = useCallback(() => {
    const node = tabsCarouselRef.current;
    if (!node) return 0;

    const firstTab = node.querySelector('[role="tab"]');
    const tabList = node.querySelector('[role="tablist"]');

    if (!firstTab) {
      return node.clientWidth / 3;
    }

    const tabWidth = firstTab.getBoundingClientRect().width;
    const gapValue = tabList ? window.getComputedStyle(tabList).columnGap || window.getComputedStyle(tabList).gap : "0";
    const gap = Number.parseFloat(gapValue) || 0;

    return tabWidth + gap;
  }, []);

  const scrollTabsCarousel = useCallback((direction) => {
    const node = tabsCarouselRef.current;
    if (!node) return;
    const tabStepWidth = getTabsStepWidth() || node.clientWidth / 3;
    node.scrollBy({ left: direction * tabStepWidth, behavior: "smooth" });
    window.requestAnimationFrame(() => {
      window.setTimeout(updateTabsScrollState, 180);
    });
  }, [getTabsStepWidth, updateTabsScrollState]);

  const handleTabsPointerDown = useCallback((event) => {
    const node = tabsCarouselRef.current;
    if (!node) return;

    setIsDraggingTabs(true);
    setTabsDragStartX(event.clientX);
    setTabsScrollStartLeft(node.scrollLeft);
  }, []);

  const handleTabsPointerMove = useCallback((event) => {
    if (!isDraggingTabs) return;
    const node = tabsCarouselRef.current;
    if (!node) return;

    const deltaX = event.clientX - tabsDragStartX;
    node.scrollLeft = tabsScrollStartLeft - deltaX;
  }, [isDraggingTabs, tabsDragStartX, tabsScrollStartLeft]);

  const handleTabsPointerUp = useCallback(() => {
    setIsDraggingTabs(false);
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      try {
        const { effectiveRole, listLevel } = await fetchUserLevel();
        await Promise.all([
          fetchCameras(),
          fetchAlgorithms(),
          effectiveRole >= 3 ? fetchUsers() : Promise.resolve()
        ]);

        await fetchInfractions(false, listLevel);
      } catch (error) {
        console.error('Erro ao inicializar dados:', error);
        toast({
          title: "Erro ao carregar dados",
          description: "Erro ao inicializar a tela",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setInitialLoading(false);
      }
    };

    initializeData();
  }, []);

  const fetchUserLevel = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      const response = await fetch('/api/users/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return { effectiveRole: 1, listLevel: 1, externallyRestricted: false };
        }
        throw new Error('Erro ao buscar informações do usuário');
      }

      const data = await response.json();
      const effectiveRole = data.effectiveRole ?? data.role;
      const restricted = Boolean(data.externallyRestricted);
      // Em acesso externo restrito, lista geral (nível 3) só para leitura
      const listLevel = restricted ? 3 : effectiveRole;
      setUserLevel(effectiveRole);
      setExternallyRestricted(restricted);
      return { effectiveRole, listLevel, externallyRestricted: restricted };
    } catch (error) {
      console.error('Erro ao buscar nível do usuário:', error);
      setUserLevel(1);
      setExternallyRestricted(false);
      return { effectiveRole: 1, listLevel: 1, externallyRestricted: false };
    }
  };

  const fetchCameras = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      const response = await fetch('/api/cameras', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        throw new Error('Erro ao buscar câmeras');
      }

      const data = await response.json();
      setCameras(data);
    } catch (error) {
      console.error('Erro ao buscar câmeras:', error);
      toast({
        title: "Erro ao carregar câmeras",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchAlgorithms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/algorithms', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar algoritmos');

      const data = await response.json();
      setAlgorithms(data);
    } catch (error) {
      console.error('Erro ao buscar algoritmos:', error);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar usuários');

      const data = await response.json();
      setUsers(data || []);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const getUserName = (userId) => {
    if (!userId) return 'Usuário desconhecido';
    const normalizedUserId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
    const user = users.find(currentUser => currentUser.id === normalizedUserId);
    return user ? (user.username || user.name || `Usuário ${user.id}`) : `Usuário ID: ${userId}`;
  };

  const refreshInfractions = async () => {
    setCurrentFilters(null); // Limpar filtros ao atualizar
    await fetchInfractions(false, listUserLevel);
    toast({
      title: "Infrações atualizadas",
      description: "Carregadas as infrações mais recentes",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };
  // Nova função para buscar últimas 24h
  const handleLast24Hours = async () => {
    setCurrentFilters(null); // Limpar filtros ao buscar últimas 24h
    const onlyMine = !externallyRestricted && userLevel <= 2;
    await fetchLast24HoursInfractions(onlyMine);
    toast({
      title: "Infrações das últimas 24h",
      description: onlyMine ? "Carregadas suas análises das últimas 24h" : "Carregadas infrações das últimas 24h",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const getCameraName = (cameraId) => {
    if (!cameraId) return 'Câmera desconhecida';
    const cameraIdNum = typeof cameraId === 'string' ? parseInt(cameraId, 10) : cameraId;
    const camera = cameras.find(cam => cam.id === cameraIdNum);
    return camera ? camera.name : `Câmera ID: ${cameraId}`;
  };

  const getAlgorithmName = (algorithmId) => {
    if (!algorithmId) return 'Algoritmo desconhecido';
    const algorithmIdNum = typeof algorithmId === 'string' ? parseInt(algorithmId, 10) : algorithmId;
    const algorithm = algorithms.find(alg => alg.id === algorithmIdNum);
    return algorithm ? algorithm.name : `Algoritmo ID: ${algorithmId}`;
  };

  const openImageModal = (imageUrl, infraction) => {
    setSelectedImage(imageUrl);
    setSelectedInfraction(infraction);

    // CORREÇÃO: Encontrar o índice da infração selecionada
    const infractionIndex = filteredInfractions.findIndex(inf => inf.id === infraction.id);
    setCurrentInfractionIndex(infractionIndex >= 0 ? infractionIndex : 0);

    setIsModalOpen(true);
  }; const applyAdvancedFilters = async () => {
    console.log('Aplicando filtros:', filters);

    // Verificar se pelo menos um filtro foi preenchido - corrigir a verificação
    const hasFilters = Object.entries(filters).some(([key, value]) => {
      // Para campos boolean (como plateContains), não verificar como string
      if (typeof value === 'boolean') return false;
      // Para outros campos, verificar se é string e não está vazio
      return value && typeof value === 'string' && value.trim() !== '';
    });

    if (!hasFilters) {
      toast({
        title: "Nenhum filtro aplicado",
        description: "Preencha pelo menos um campo para filtrar",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Criar uma cópia dos filtros para armazenar
    const filtersToApply = { ...filters };

    // Mesclar data + hora se fornecidas
    if (filtersToApply.startDate) {
      const time = filtersToApply.startTime && filtersToApply.startTime.trim() !== '' ? filtersToApply.startTime : '00:00:00';
      filtersToApply.startDate = `${filtersToApply.startDate} ${time}`;
    }
    if (filtersToApply.endDate) {
      const time = filtersToApply.endTime && filtersToApply.endTime.trim() !== '' ? filtersToApply.endTime : '23:59:59.999';
      // backend pode esperar fim do dia; ajuste conforme necessário
      filtersToApply.endDate = `${filtersToApply.endDate} ${time}`;
    }

    // Remove campos temporários se necessário (se backend não suporta startTime/endTime)
    delete filtersToApply.startTime;
    delete filtersToApply.endTime;

    // Chamar a função que faz a busca com filtersToApply
    await searchInfractions(filtersToApply);
    setCurrentFilters(filtersToApply); // Armazenar os filtros aplicados
    onClose();

    // O toast será atualizado pelo useEffect quando as estatísticas forem calculadas
    toast({
      title: "Filtros aplicados",
      description: "Busca realizada com sucesso",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const resetFilters = async () => {
    const emptyFilters = {
      status: "",
      cameraId: "",
      algorithmId: "",
      startDate: "",
      endDate: "",
      plate: "",
      infractionClass: "",
      reviewedBy: "",
      plateContains: false, // Incluir no reset
      startTime: "",
      endTime: ""
    };

    setFilters(emptyFilters);
    setCurrentFilters(null); // Limpar filtros armazenados

    // Voltar à busca normal de infrações
    await fetchInfractions(false, listUserLevel);

    toast({
      title: "Filtros limpos",
      description: "Exibindo todas as infrações",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  // Função para exportar dados da busca
  const handleExportData = async (format = 'pdf') => {
    if (!currentFilters) {
      toast({
        title: "Nenhum filtro aplicado",
        description: "Aplique filtros antes de exportar",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsExporting(true);
    try {
      if (format === 'pdf') {
        await exportToPDF(currentFilters, filteredInfractions, searchStats);
      } else {
        await exportSearchData(currentFilters);
      }
    } catch (error) {
      console.error('Erro na exportação:', error);
    } finally {
      setIsExporting(false);
    }
  };
  const loadMoreInfractions = () => {
    // Verificar qual tipo de filtro está ativo baseado no estado da paginação
    if (pagination.activeFilter === 'search' && currentFilters) {
      // Se há filtros de busca ativos, continuar com a busca filtrada
      searchInfractionsLoadMore(currentFilters);
    } else if (pagination.activeFilter === 'last24h') {
      // Se está mostrando últimas 24h, continuar com essa busca
      const onlyMine = !externallyRestricted && userLevel <= 2;
      fetchLast24HoursLoadMore(onlyMine);
    } else {
      // Busca normal
      fetchInfractions(true, listUserLevel);
    }
  };

  // Função para carregar mais infrações durante navegação no modal
  const handleLoadMoreInModal = useCallback(async () => {
    if (!currentFilters) {
      // Busca normal - passar nível de listagem
      await loadMoreInfractions(listUserLevel);
    } else {
      // Busca com filtros
      await searchInfractionsLoadMore(currentFilters);
    }
  }, [currentFilters, loadMoreInfractions, searchInfractionsLoadMore, listUserLevel]);

  // Estado para controlar aba ativa e se a página foi rolada
  const [activeTab, setActiveTab] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let rafId = null;
    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 80);
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // verificar estado inicial (caso a página já esteja rolada)
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    updateTabsScrollState();

    const onResize = () => updateTabsScrollState();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [updateTabsScrollState, userLevel]);

  // Ajustar aba ativa quando o userLevel mudar
  useEffect(() => {
    // Se o usuário tem nível baixo (<2), começar na aba "Lista de Infrações"
    if (userLevel < 2) {
      setActiveTab(0); // Lista de Infrações será o índice 0
    }
  }, [userLevel]);

  useEffect(() => {
    updateTabsScrollState();
    const handleResize = () => updateTabsScrollState();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateTabsScrollState, userLevel]);

  useEffect(() => {
    if (tabsCarouselRef.current) {
      tabsCarouselRef.current.scrollTo({ left: 0, behavior: "auto" });
    }
  }, [userLevel]);

  // Calcular o índice da aba "Lista de Infrações" baseado nas abas visíveis
  const listTabIndex = Math.max(0, tabsItems.findIndex((item) => item.label === "Lista de Infrações"));

  return (
    <Box p={{ base: 3, md: 6 }} flex="1" minH="100vh" bg="gray.100" w="100%" maxW="100%" overflowX="hidden">
     <Button
       position="fixed"
       top={{ base: "76px", md: "16px" }}
       right={{ base: "12px", md: "16px" }}
       zIndex="overlay"
       colorScheme="teal"
       onClick={onOpen}
       leftIcon={<FilterIcon />}
       borderRadius="md"
       boxShadow="md"
       transition="opacity 200ms ease, transform 200ms ease"
      opacity={(isScrolled && activeTab === listTabIndex) ? 1 : 0}
      transform={(isScrolled && activeTab === listTabIndex) ? 'translateY(0)' : 'translateY(-8px)'}
      pointerEvents={(isScrolled && activeTab === listTabIndex) ? 'auto' : 'none'}
      size={{ base: "sm", md: "md" }}
     >
       Filtros
     </Button>
      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={3} mb={4}>
        <Heading display="flex" alignItems="center" fontSize={{ base: "xl", md: "2xl" }}>
          <WarningIcon />
          Infrações Detectadas
        </Heading>
        {/* Botões visíveis apenas quando a aba "Lista de Infrações" estiver ativa */}
        <Wrap
          transition="opacity 200ms ease, transform 200ms ease"
          opacity={activeTab === listTabIndex ? 1 : 0}
          transform={activeTab === listTabIndex ? 'translateY(0)' : 'translateY(-6px)'}
          pointerEvents={activeTab === listTabIndex ? 'auto' : 'none'}
          spacing={2}
          justify={{ base: "flex-start", md: "flex-end" }}
        >
          <WrapItem>
            <Button
              size={{ base: "sm", md: "md" }}
              colorScheme="green"
              onClick={refreshInfractions}
              leftIcon={<RefreshIcon />}
              isLoading={loading}
              loadingText="Atualizando"
            >
              Atualizar
            </Button>
          </WrapItem>
          <WrapItem>
            <Button
              size={{ base: "sm", md: "md" }}
              colorScheme="blue"
              leftIcon={<SearchIcon />}
              onClick={handleLast24Hours}
            >
              Últimas 24h
            </Button>
          </WrapItem>
          <WrapItem>
            <Button size={{ base: "sm", md: "md" }} colorScheme="teal" onClick={onOpen} leftIcon={<FilterIcon />}>
              Filtros Avançados
            </Button>
          </WrapItem>
        </Wrap>
      </Flex>

      {externallyRestricted && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon />
          Acesso externo: você pode apenas visualizar infrações. Validação e lotes ficam disponíveis na rede interna.
        </Alert>
      )}

      {/* Tabs controladas para manter activeTab sincronizado (necessário para controlar visibilidade do botão) */}
      <Tabs variant="soft-rounded" colorScheme="red" mb={4} index={activeTab} onChange={(index) => setActiveTab(index)} isLazy lazyBehavior="unmount" w="100%" maxW="100%" minW={0}>
        <Box position="relative" w="100%" maxW="100%" minW={0}>
          <Box
            ref={tabsCarouselRef}
            overflowX="auto"
            pb={1}
            minW={0}
            mx={{ base: 9, md: 0 }}
            onScroll={updateTabsScrollState}
            onPointerDown={handleTabsPointerDown}
            onPointerMove={handleTabsPointerMove}
            onPointerUp={handleTabsPointerUp}
            onPointerLeave={handleTabsPointerUp}
            cursor={{ base: isDraggingTabs ? "grabbing" : "grab", md: "default" }}
            touchAction="pan-x"
            sx={{
              scrollSnapType: { base: "x mandatory", md: "none" },
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none"
            }}
          >
            <TabList minW={{ base: "100%", md: "max-content" }} flexWrap="nowrap" gap={{ base: 2, md: 0 }}>
            {tabsItems.map((tabItem) => (
              <Tab
                key={tabItem.label}
                flex={{ base: "0 0 calc((100% - 1rem) / 3)", md: "0 0 auto" }}
                scrollSnapAlign={{ base: "center", md: "none" }}
                fontSize={{ base: "xs", md: "sm" }}
                px={{ base: 2, md: 3 }}
                py={{ base: 2, md: 2 }}
                minH={{ base: "56px", md: "auto" }}
                whiteSpace="normal"
                textAlign="center"
                lineHeight="1.2"
                borderRadius={{ base: "xl", md: "md" }}
                borderWidth={{ base: "1px", md: "0" }}
                borderColor={{ base: "gray.200", md: "transparent" }}
                bg={{ base: "white", md: "transparent" }}
                _selected={{
                  bg: { base: "red.500", md: "red.100" },
                  color: { base: "white", md: "red.700" },
                  borderColor: { base: "red.500", md: "transparent" },
                  boxShadow: { base: "sm", md: "none" }
                }}
              >
                {tabItem.label}
              </Tab>
            ))}
            </TabList>
          </Box>

          <IconButton
            aria-label="Rolar abas para a esquerda"
            icon={<ChevronLeftIcon boxSize={5} />}
            size="sm"
            position="absolute"
            left="0"
            top="50%"
            transform="translateY(-50%)"
            display={{ base: "inline-flex", md: "none" }}
            borderRadius="full"
            boxShadow="md"
            bg="white"
            _hover={{ bg: "gray.50" }}
            onClick={() => scrollTabsCarousel(-1)}
            isDisabled={!canScrollTabsLeft}
            zIndex={2}
          />

          <IconButton
            aria-label="Rolar abas para a direita"
            icon={<ChevronRightIcon boxSize={5} />}
            size="sm"
            position="absolute"
            right="0"
            top="50%"
            transform="translateY(-50%)"
            display={{ base: "inline-flex", md: "none" }}
            borderRadius="full"
            boxShadow="md"
            bg="white"
            _hover={{ bg: "gray.50" }}
            onClick={() => scrollTabsCarousel(1)}
            isDisabled={!canScrollTabsRight}
            zIndex={2}
          />
        </Box>

        <TabPanels w="100%" maxW="100%" minW={0}>
          {userLevel >= 2 && (
            <TabPanel px={{ base: 0, md: 4 }} w="100%" maxW="100%" minW={0}>
              <PeriodStatsTab userLevel={userLevel} />
            </TabPanel>
          )}

          {userLevel > 2 && (
            <TabPanel px={{ base: 0, md: 4 }} w="100%" maxW="100%" minW={0}>
              <UserStatsTab userLevel={userLevel} />
            </TabPanel>
          )}

          {userLevel >= 3 && (
            <TabPanel px={{ base: 0, md: 4 }} w="100%" maxW="100%" minW={0}>
              <AnalysisTab />
            </TabPanel>
          )}

          {userLevel >= 2 && (
            <TabPanel px={{ base: 0, md: 4 }} w="100%" maxW="100%" minW={0}>
              <ValidationTab userLevel={userLevel} />
            </TabPanel>
          )}

          <TabPanel px={{ base: 0, md: 4 }} w="100%" maxW="100%" minW={0}>
            {/* Indicador de filtros ativos e estatísticas */}
            {currentFilters && (
              <Box mb={4} p={4} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
                <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={3} mb={3}>
                  <Text fontSize="sm" color="blue.700" fontWeight="medium" lineHeight="1.5">
                    Filtros ativos: {Object.entries(currentFilters)
                      .filter(([key, value]) => {
                        // Para campos boolean, verificar se é true
                        if (typeof value === 'boolean') return value;
                        // Para outros campos, verificar se é string e não está vazio
                        return value && typeof value === 'string' && value.trim() !== '';
                      })
                      .map(([key, value]) => {
                        if (key === 'cameraId') return `Câmera: ${getCameraName(value)}`;
                        if (key === 'algorithmId') return `Algoritmo: ${getAlgorithmName(value)}`;
                        if (key === 'reviewedBy') return `Revisado por: ${getUserName(value)}`;
                        if (key === 'status') return `Status: ${value}`;
                        if (key === 'plate') return `Placa: ${value}`;
                        if (key === 'plateContains') return `Busca por conteúdo: ${value ? 'Sim' : 'Não'}`;
                        if (key === 'startDate') return `De: ${new Date(value).toLocaleDateString('pt-BR')}`;
                        if (key === 'endDate') return `Até: ${new Date(value).toLocaleDateString('pt-BR')}`;
                        return `${key}: ${value}`;
                      })
                      .join(', ')
                    }
                  </Text>
                  <Wrap spacing={2}>
                    {!isReadOnlyView && (
                    <Menu>
                      <MenuButton 
                        as={Button}
                        size="sm" 
                        colorScheme="green" 
                        leftIcon={<DownloadIcon />}
                        rightIcon={<ChevronDownIcon />}
                        isLoading={isExporting}
                        loadingText="Exportando..."
                      >
                        Baixar dados da busca
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={() => handleExportData('pdf')}>
                          <DownloadIcon mr={2} />
                          Relatório PDF (com imagens)
                        </MenuItem>
                        <MenuItem onClick={() => handleExportData('zip')}>
                          <DownloadIcon mr={2} />
                          Arquivo ZIP (dados + imagens)
                        </MenuItem>
                      </MenuList>
                    </Menu>
                    )}
                    <Button size="sm" onClick={resetFilters} colorScheme="blue" variant="outline">
                      Limpar Filtros
                    </Button>
                  </Wrap>
                </Flex>
                
                {/* Exibir estatísticas da busca */}
                {searchStats && (
                  <Grid templateColumns="repeat(auto-fit, minmax(120px, 1fr))" gap={3} mt={3}>
                    <Box textAlign="center" bg="white" p={2} borderRadius="md" boxShadow="sm">
                      <Text fontSize="lg" fontWeight="bold" color="blue.600">{searchStats.total}</Text>
                      <Text fontSize="xs" color="gray.600">Total</Text>
                    </Box>
                    <Box textAlign="center" bg="white" p={2} borderRadius="md" boxShadow="sm">
                      <Text fontSize="lg" fontWeight="bold" color="green.600">{searchStats.confirmed}</Text>
                      <Text fontSize="xs" color="gray.600">Confirmadas</Text>
                    </Box>
                    <Box textAlign="center" bg="white" p={2} borderRadius="md" boxShadow="sm">
                      <Text fontSize="lg" fontWeight="bold" color="red.600">{searchStats.rejected}</Text>
                      <Text fontSize="xs" color="gray.600">Rejeitadas</Text>
                    </Box>
                    <Box textAlign="center" bg="white" p={2} borderRadius="md" boxShadow="sm">
                      <Text fontSize="lg" fontWeight="bold" color="orange.600">{searchStats.pending}</Text>
                      <Text fontSize="xs" color="gray.600">Pendentes</Text>
                    </Box>
                  </Grid>
                )}
              </Box>
            )}

            {initialLoading || loading ? (
              <Flex justify="center" my={12}>
                <Spinner size="xl" />
              </Flex>
            ) : filteredInfractions.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={12} bg="white" borderRadius="md" boxShadow="sm">
                <Text fontSize="xl" mb={4}>
                  {!externallyRestricted && userLevel <= 2
                    ? "Você ainda não analisou nenhuma infração" 
                    : "Nenhuma infração encontrada"
                  }
                </Text>
                {!externallyRestricted && userLevel <= 2 ? (
                  <Text fontSize="md" color="gray.600" textAlign="center" mb={4}>
                    Infrações aparecerão aqui após você analisá-las em lotes atribuídos a você.
                  </Text>
                ) : (
                  <Button colorScheme="teal" onClick={resetFilters}>Limpar Filtros</Button>
                )}
              </Flex>
            ) : (
              <Grid templateColumns={{ base: "repeat(2, minmax(0, 1fr))", lg: "repeat(auto-fill, minmax(300px, 1fr))" }} gap={{ base: 3, md: 6 }}>
                {filteredInfractions.map((infraction) => (
                  <PlateInfractionCard
                    key={infraction.id}
                    infraction={infraction}
                    openImageModal={openImageModal}
                    getAlgorithmName={getAlgorithmName}
                    getCameraName={getCameraName}
                  />
                ))}
              </Grid>
            )}

            {pagination.hasMore && (
              <Flex justify="center" mt={4} mb={6}>
                <Button
                  onClick={loadMoreInfractions}
                  isLoading={loadingMore}
                  loadingText="Carregando..."
                  colorScheme="blue"
                  size="lg"
                  width={{ base: "100%", md: "200px" }}
                  maxW="320px"
                >
                  Ver Mais
                </Button>
              </Flex>
            )}
          </TabPanel>

          {userLevel >= 4 && (
            <TabPanel px={{ base: 0, md: 4 }} w="100%" maxW="100%" minW={0}>
              <UploadInfractionsTab />
            </TabPanel>
          )}

          {userLevel >= 5 && (
            <TabPanel px={{ base: 0, md: 4 }} w="100%" maxW="100%" minW={0}>
              <TrainingExportTab />
            </TabPanel>
          )}

          {userLevel >= 2 && (
            <TabPanel px={{ base: 0, md: 4 }} w="100%" maxW="100%" minW={0}>
              <RegisteredPlatesTab />
            </TabPanel>
          )}

          {userLevel >= 2 && (
            <TabPanel px={{ base: 0, md: 4 }} w="100%" maxW="100%" minW={0}>
              <WhitelistTab />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* ImageModal */}
      {selectedInfraction && selectedImage && (
        <ImageModal
          isOpen={!!selectedInfraction}
          onClose={() => {
            setSelectedInfraction(null);
            setSelectedImage(null);
            setCurrentInfractionIndex(-1);
          }}
          selectedImage={selectedImage}
          selectedInfraction={selectedInfraction}
          updateInfractionStatus={updateInfractionStatus}
          getAlgorithmName={getAlgorithmName}
          getCameraName={getCameraName}
          infractions={filteredInfractions}
          currentIndex={currentInfractionIndex}
          setCurrentIndex={setCurrentInfractionIndex}
          loadMoreInfractions={handleLoadMoreInModal}
          hasMore={pagination.hasMore}
          isLoadingMore={loadingMore}
          userLevel={userLevel}
          readOnly={isReadOnlyView}
        />
      )}

      <FilterDrawer
        isOpen={isOpen}
        onClose={onClose}
        filters={filters}
        handleFilterChange={handleFilterChange}
        cameras={cameras}
        algorithms={algorithms}
          users={users}
          loadingUsers={loadingUsers}
          showReviewerFilter={userLevel >= 3}
        applyAdvancedFilters={applyAdvancedFilters}
        resetFilters={resetFilters}
      />
    </Box>
  );
};

export default InfractionsMain;